/**
 * Signage design tokens — TypeScript port.
 *
 * Authoritative spec: `specs/modules/signage-display/signage-design-tokens-spec.md`.
 * Keep this file in lockstep with `apps/signage-tv/lib/design/tokens.dart`
 * and `public/signage-player/src/styles.css` CSS custom properties.
 * Changing values here MUST be mirrored in all three surfaces in one
 * iteration.
 */

/** --- Colors -------------------------------------------------------- */

export const SignageColors = {
  // Surface
  surfaceCanvas: '#0F172A',
  surfaceCard: '#1E293B',
  surfaceTile: 'rgba(255, 255, 255, 0.06)',
  surfaceDivider: 'rgba(255, 255, 255, 0.08)',
  surfaceOverlay: 'rgba(0, 0, 0, 0.54)',

  // Content
  contentPrimary: '#FFFFFF',
  contentMuted: 'rgba(255, 255, 255, 0.62)',
  contentDim: 'rgba(255, 255, 255, 0.38)',
  contentOnAccent: '#FFFFFF',

  // Progress-celebration artwork (light card on the dark signage canvas)
  celebrationSurface: '#F0FDFA',
  celebrationText: '#134E4A',
  celebrationMuted: '#5C8A87',
  celebrationHighlight: '#F59E0B',

  // Semantic
  semanticSuccess: '#16A34A',
  semanticWarning: '#D97706',
  semanticError: '#DC2626',
  // semantic.accent is org-overridable — consume via branding.primaryColor.
} as const;

/** --- Typography ---------------------------------------------------- */

export interface TypographyToken {
  readonly fontSize: number;
  readonly fontWeight: number;
  readonly lineHeight?: number;
  readonly letterSpacing?: number;
}

const BASE_TYPOGRAPHY = {
  displayXl: { fontSize: 84, fontWeight: 700, lineHeight: 1.08 },
  displayLg: { fontSize: 72, fontWeight: 700, lineHeight: 1.08 },
  displayMd: { fontSize: 56, fontWeight: 700, lineHeight: 1.1 },
  titleXl: { fontSize: 48, fontWeight: 700 },
  titleLg: { fontSize: 36, fontWeight: 700 },
  titleMd: { fontSize: 32, fontWeight: 600 },
  titleSm: { fontSize: 28, fontWeight: 600 },
  bodyLg: { fontSize: 32, fontWeight: 400, lineHeight: 1.4 },
  bodyMd: { fontSize: 24, fontWeight: 400 },
  bodySm: { fontSize: 22, fontWeight: 600 },
  bodyXs: { fontSize: 18, fontWeight: 500 },
  caption: { fontSize: 14, fontWeight: 500 },
  captionXs: { fontSize: 13, fontWeight: 400 },
  overline: { fontSize: 14, fontWeight: 700, letterSpacing: 2 },
} as const satisfies Record<string, TypographyToken>;

export type TypographyKey = keyof typeof BASE_TYPOGRAPHY;

/**
 * Compute the scaled typography table for the current viewport. Call once
 * at boot + on window resize (rendering.ts applies result by setting
 * font-size on the root).
 */
export function scaledTypography(
  viewportHeightPx: number = window.innerHeight
): Record<TypographyKey, TypographyToken> {
  const f = scaleFactor(viewportHeightPx);
  const out = {} as Record<TypographyKey, TypographyToken>;
  for (const [key, token] of Object.entries(BASE_TYPOGRAPHY)) {
    out[key as TypographyKey] = {
      ...token,
      fontSize: Math.round(token.fontSize * f),
    };
  }
  return out;
}

/** Current resolution scaling factor. */
export function scaleFactor(viewportHeightPx: number = window.innerHeight): number {
  if (viewportHeightPx >= 2000) return 1.5;
  if (viewportHeightPx < 900) return 0.75;
  return 1.0;
}

/** --- Spacing ------------------------------------------------------- */

export const SignageSpacing = {
  s0: 0,
  s1: 4,
  s2: 8,
  s3: 12,
  s4: 16,
  s5: 20,
  s6: 24,
  s7: 32,
  s8: 48,
  s9: 64,
  s10: 96,
} as const;

/** --- Radius -------------------------------------------------------- */

export const SignageRadius = {
  sm: 10,
  md: 12,
  lg: 16,
  xl: 20,
  r2xl: 24,
  pill: 999,
} as const;

/** --- Border -------------------------------------------------------- */

export const SignageBorder = {
  hairline: 1,
  thin: 2,
  thick: 4,
} as const;

/** --- Motion -------------------------------------------------------- */

export const SignageMotion = {
  quickMs: 150,
  standardMs: 450,
  emphasisMs: 600,
  celebrationMs: 1200,
  thermometerMs: 600,
  curveStandard: 'ease',
  curveEmphasis: 'ease-out',
  curveCelebration: 'cubic-bezier(0.22, 1, 0.36, 1)',
} as const;

/**
 * Returns 0 when prefers-reduced-motion is set, else [baseMs]. Used by
 * JavaScript setTimeout calls; CSS transitions are already zeroed via
 * `@media (prefers-reduced-motion: reduce)` in styles.css.
 */
export function durationMsFor(baseMs: number): number {
  if (typeof window === 'undefined' || !window.matchMedia) return baseMs;
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  return mq.matches ? 0 : baseMs;
}

/** --- Z-index ------------------------------------------------------- */

export const SignageZ = {
  panel: 0,
  overlayBanner: 10,
  overlayCelebration: 50,
  overlayDiagnostic: 200,
  overlayUpgrade: 300,
} as const;

/**
 * Write tokens as CSS custom properties on a root element. Called once at
 * boot from main.ts. Keeps styles.css authorable in familiar CSS while
 * letting tests + runtime read the same table.
 */
export function applyTokensToRoot(root: HTMLElement): void {
  const style = root.style;
  style.setProperty('--sig-celebration-surface', SignageColors.celebrationSurface);
  style.setProperty('--sig-celebration-text', SignageColors.celebrationText);
  style.setProperty('--sig-celebration-muted', SignageColors.celebrationMuted);
  style.setProperty('--sig-celebration-highlight', SignageColors.celebrationHighlight);
  // Spacing
  for (const [key, val] of Object.entries(SignageSpacing)) {
    style.setProperty(`--sig-${toKebab(key)}`, `${val}px`);
  }
  // Radius
  for (const [key, val] of Object.entries(SignageRadius)) {
    style.setProperty(`--sig-radius-${toKebab(key)}`, `${val}px`);
  }
  // Border
  for (const [key, val] of Object.entries(SignageBorder)) {
    style.setProperty(`--sig-border-${toKebab(key)}`, `${val}px`);
  }
  // Motion
  style.setProperty('--sig-motion-quick', `${SignageMotion.quickMs}ms`);
  style.setProperty('--sig-motion-standard', `${SignageMotion.standardMs}ms`);
  style.setProperty('--sig-motion-emphasis', `${SignageMotion.emphasisMs}ms`);
  style.setProperty('--sig-motion-celebration', `${SignageMotion.celebrationMs}ms`);
  style.setProperty('--sig-motion-thermometer', `${SignageMotion.thermometerMs}ms`);
}

function toKebab(camel: string): string {
  // Insert a dash before interior uppercase letters only — digits stay put
  // ('s1' → 's1', 'r2xl' → 'r2xl', 'sm' → 'sm').
  return camel.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
